<template>
  <section aria-labelledby="search-results-header">
    <h1
      id="search-results-header"
      class="mb-2 font-normal text-theme-600 flex items-center text-xl"
    >
      Search Results <template v-if="numberOfResults">({{ numberOfResults }})</template>
    </h1>
    <SearchInput
      id="search-input"
      placeholder="Search . . ."
      :is-loading="isLoading"
      :value="searchQuery"
      data-test="inline-search"
      @input="handleSearch"
    />
    <SearchOptions
      class="pt-4"
      :search-entities="searchEntities"
      @input="updateEnabledSearchEntity"
    />
    <div id="search-status" aria-live="polite">
      <MediaCard
        v-if="isLoading"
        class="mt-12"
        description="Loading results..."
      >
        &nbsp;
      </MediaCard>
      <MediaCard
        v-if="!numberOfResults && !isLoading && searchQuery"
        class="mt-12"
        description="No Results"
      >
        &nbsp;
      </MediaCard>
      <p v-if="!isLoading && numberOfResults">
        {{ numberOfResults }} results found. Showing {{ resultsStart + 1 }} - {{ resultsEnd }}.
      </p>
    </div>
    <!-- If there is no searchQuery, we fallback to empty state display -->
    <MediaCard
      v-if="!searchQuery && !isLoading"
      class="mt-12"
      title="What are you searching for?"
      sub-title="Enter a phrase to search through the entire catalog"
    >
      &nbsp;
    </MediaCard>
    <section
      v-if="!isLoading && searchQuery"
      id="search-results"
      class="mt-8"
      role="list"
      tabindex="0"
      aria-label="Search results list"
    >
      <NuxtLink
        v-for="result of results"
        :key="result.id"
        role="listitem"
        :to="{ path: result.url }"
        :aria-labelledby="result.url"
        :title="getDisplayName(result)"
        class="block text-left mb-4"
        style="text-decoration: none;"
      >
        <MediaCard
          :title="getDisplayName(result)"
          :sub-title="getDocumentTypeLabel(result.documentType)"
          :description="result.displayDescription || 'No description available'"
          :data-test="result.id + '-test'"
        />
      </NuxtLink>
    </section>
    <BasePagination
      v-if="!isLoading && numberOfResults > pageSize"
      :current-page="currentPage"
      :total-pages="totalPages"
      data-test="pagination-test"
      class="mb-6"
      @page-change="updatePage"
    />
  </section>
</template>

<script>
import { mapState } from 'vuex'
import { ref } from '@vue/composition-api'
import SearchInput from '@/components/SearchInput'
import { debounce, decamelize, capitalize } from '@/utils/generalUtils'
import MediaCard from '@/components/MediaCard'
import SearchOptions from '@/components/SearchOptions'
import { LIMIT_MEDIUM } from '@/constants/pagination'
import useGlobalSearch from '@/composables/useGlobalSearch'
import useInterpolation from '@/composables/useInterpolation'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { TITLE_INTERPOLATIONS } from '@/constants/featureFlags'
import { generateProgramLabel, generateCourseLabel } from '@/utils/dataLabels'

const SEARCH_DEBOUNCED_TIME = 1000
const PAGE_NAME = 'search'

export default {
  components: {
    SearchInput,
    MediaCard,
    SearchOptions,
  },
  setup() {
    const { searchEntities: updatedSearchEntities } = useGlobalSearch()
    const searchEntities = ref(updatedSearchEntities)
    const { getInterpolatedFieldValue } = useInterpolation()
    const { enableTitleInterpolations } = useFeatureFlags({
      enableTitleInterpolations: TITLE_INTERPOLATIONS,
    })
    return {
      searchEntities,
      getInterpolatedFieldValue,
      enableTitleInterpolations,
    }
  },
  data() {
    return {
      results: [],
      numberOfResults: 0,
      isLoading: false,
      pageSize: LIMIT_MEDIUM,
      searchQuery: this.$route.query.q,
      searchDebouncedTime: 1000,
    }
  },
  computed: {
    ...mapState('settings', {
      activeCatalog: (state) => state.activeCatalog,
      items: (state) => state.nav,
    }),
    ...mapState('settings', ['school', 'host']),
    enabledSearchEntitiesString() {
      return this.searchEntities
        .filter((el) => el.enabled)
        .map((el) => el.id)
        .join(',')
    },
    currentPage() {
      return this.$route.query.page ? parseInt(this.$route.query.page, 10) : 1
    },
    totalPages() {
      return Math.ceil(this.numberOfResults / LIMIT_MEDIUM)
    },
    resultsStart() {
      return (this.currentPage - 1) * LIMIT_MEDIUM
    },
    resultsEnd() {
      const end = this.resultsStart + LIMIT_MEDIUM
      return this.numberOfResults < end ? this.numberOfResults : end
    },
  },
  watch: {
    '$route.query': 'fetchData',
    '$route.query.q'() {
      this.searchQuery = this.$route.query.q
    },
  },
  created() {
    this.updateEnabledSearchEntities()
    this.$store.commit('page/SET_CURRENT_PAGE_LABEL', 'Search')
    this.fetchData()
  },
  methods: {
    capitalize,
    decamelize,
    getDocumentTypeLabel(type) {
      const searchEntity = this.searchEntities.find((el) => el.id === type)
      if (searchEntity) return searchEntity.label
      return capitalize(decamelize(type))
    },
    handleSearch(v) {
      this.searchQuery = v
      if (v) {
        this.searchDebounced(v)
      } else {
        this.results = []
        this.numberOfResults = 0
        this.isLoading = false
      }
    },
    updateEnabledSearchEntity(value) {
      const entity = this.searchEntities.find(({ id }) => id === value.id)
      entity.enabled = !entity.enabled
      return this.search()
    },
    searchDebounced: debounce(function(v) {
      this.search()
    }, SEARCH_DEBOUNCED_TIME),
    search() {
      this.$router.push({
        name: PAGE_NAME,
        query: {
          page: 1,
          ...this.$route.query,
          q: this.searchQuery,
          searchEntities: this.enabledSearchEntitiesString,
        },
      })
    },
    updatePage(page) {
      this.$router.push({
        name: PAGE_NAME,
        query: {
          ...this.$route.query,
          page,
        },
      })
    },
    async fetchData() {
      this.updateEnabledSearchEntities()
      const { q, page = 1 } = this.$route.query
      if (!q) return
      this.isLoading = true

      const limit = LIMIT_MEDIUM
      const skip = Math.max(0, page - 1) * LIMIT_MEDIUM

      const url = `/ca/${this.school}/globalSearch/${this.activeCatalog}/${encodeURIComponent(q)}`
      try {
        const { data = [], meta = {} } = await this.$axios.$get(url, {
          params: {
            limit,
            skip,
            searchEntities: this.enabledSearchEntitiesString,
            enableTitleInterpolations: this.enableTitleInterpolations,
            ...(this.enableTitleInterpolations && {
              catalogId: this.activeCatalog,
            }),
          },
        })
        this.results = data
        this.numberOfResults = meta.totalCount
        this.isLoading = false
      } catch (e) {
        this.$bugsnag.notify(e)
        console.warn('Error while fetching search results.')
      }
    },
    updateEnabledSearchEntities() {
      // update the "enabled" property on the searchEntity object
      // based on query params
      if (!this.$route.query.searchEntities) return
      const enabledSearchEntities = (this.$route.query.searchEntities || '')
        .split(',')
        .filter(Boolean)

      // update this.searchEntities array to reflect enabled values
      // defined in URL query
      this.searchEntities = this.searchEntities.map((el) => ({
        ...el,
        enabled: enabledSearchEntities.includes(el.id),
      }))
    },
    getDisplayName(object = {}) {
      if (!this.enableTitleInterpolations) return object.displayName || ''
      if (object.documentType === 'programs') {
        return generateProgramLabel(object, this.school, {
          getInterpolatedFieldValue: this.getInterpolatedFieldValue,
        })
      }
      if (object.documentType === 'courses') {
        return generateCourseLabel(object, undefined, {
          getInterpolatedFieldValue: this.getInterpolatedFieldValue,
        })
      }
      return object.displayName || ''
    },
  },
}
</script>
